import { AppNS } from 'app';
import { compact } from 'lodash-es';
import moment from 'moment';

import { SPECIAL_NAMES } from '../../ui/components/permits-picker/helpers/permitHelpers';

export const filterPermits = (
  data: Array<AppNS.Permit | undefined>
): Array<AppNS.Permit> => {
  const filtered = data.filter(
    (item: AppNS.Permit | undefined) => item !== undefined
  ) as Array<AppNS.Permit>;

  if (!filtered.length) {
    return [];
  }
  return filtered;
  // filter for future feature like multiple permits id's
  // const addr = filtered[0].addressInfo;
  // return filtered.filter((item: AppNS.Permit) => item.addressInfo === addr);
};

export const parsePermit = (
  data: AppNS.Params,
  jobType: string
): AppNS.Permit | undefined => {
  if (!data.recordId) {
    return;
  }

  let addressInfo = '';
  let cluster = '';

  if (typeof compact(data.addressArr)[0] === 'object') {
    addressInfo =
      ((compact(data.addressArr)[0] as AppNS.Params).address as string) || '';
    cluster =
      ((compact(data.addressArr)[0] as AppNS.Params).cluster as string) || '';
  } else {
    addressInfo = (compact(data.addressArr)[0] as string) || '';
  }

  return {
    scope: data.workDesc || '',
    id: data.recordId,
    permitNumber: data.recordId || '',
    inspections: parseInspections(data.availableInspTypes),
    name: data.availableInspGroup,
    addressInfo: getDBAddressInfo(addressInfo),
    addressId: addressInfo,
    cluster: cluster,
    inspectionGroup: data.availableInspGroup,
    hasStopWorkOrder: checkStopWorkOrder(data),
    hasTPIA: checkTPIACond(data),
    hasRecInsps: checkRecInsps(data),
    legacy: jobType !== 'special' && checkLegacy(data),
    authorizedUserId: getAuthorizedData(data).authorizedUserId,
    authorizedTo: getAuthorizedData(data).authorizedTo,
    authorizationDate: getAuthorizedData(data).authorizationDate,
    wallCheckRequired: data.wallCheckRequired,
    wallCheckComplete: data.wallCheckComplete,
  } as AppNS.Permit;
};

const getDBAddressInfo = (info: string) => {
  const arr = info.split(' ');
  return arr.slice(0, Math.max(arr.length, 5)).join(' ');
};

const parseInspections = (
  data: Array<AppNS.Params>
): Array<AppNS.Inspection> => {
  const output = [] as Array<AppNS.Inspection>;
  data.forEach((item) => {
    // const result = getDisciplineById(item.name);
    output.push({
      id: item.name,
      name: item.name,
      status: item.state === 'open' ? 'pending' : item.state,
      dcraState: 'pending',
      dcraStateRejectionComment: '',
    });
  });
  return output;
};

export const parseDBAddress = (data: string): AppNS.ProjectAddressFromDB => {
  const dataArray = data.split(' ');
  const quadrant_idx = dataArray.findIndex((d: string) =>
    ['nw', 'sw', 'se', 'ne'].includes(d.toLowerCase())
  );
  return {
    streetNumber: dataArray[0] || '',
    streetName: dataArray.slice(1, quadrant_idx - 1).join(' ') || '',
    streetType: dataArray[quadrant_idx - 1] || '',
    quadrant: dataArray[quadrant_idx] || '',
  };
};

export const parsePermitSearchAddress = (
  data: AppNS.ProjectAddress
): AppNS.Params => {
  return {
    street_name: data.streetName,
    street_no: data.streetNumber || data.streetNo,
    zip: data.zip,
    quadrant: data.quadrant,
    street_unit_no: data.unitNumber || data.streetUnitNo,
    street_type: data.streetType,
    street_no_suffix: data.streetNumberSuffix || data.streetNoSuffix,
  };
};

export const prepareJobData = (data: AppNS.Project): AppNS.Params => {
  const pickedPermits = data.pickedPermits.filter((permit) => {
    return (
      permit.isOn &&
      data.permits.find((originalPermit) => originalPermit.id === permit.id)
    );
  });

  const firstPicked = pickedPermits[0]
    ? data.permits.find((item) => item.id === pickedPermits[0].id)
    : null;

  const permits = pickedPermits.map((pickedPermit) => {
    return {
      inspection_group: pickedPermit.inspectionGroup,
      inspection_types: pickedPermit.inspections,
      wall_check_required: pickedPermit.wallCheckRequired,
      wall_check_complete: pickedPermit.wallCheckComplete,
      permit_number: pickedPermit.id,
      scope:
        data.permits.filter((permit) => permit.id === pickedPermit.id)[0]
          .scope || '',
    };
  });

  const address =
    data.dbAddress || (data.addressIds && parseDBAddress(data.addressIds[0]));

  return {
    properties: {
      client_id: data.clientId,
      address_ids: data.addressIds,
      start_date: data.startDate,
      end_date: data.endDate,
      additional_info: data.description,
      site_contact_name: data.contactName,
      site_contact_email: data.contactEmail,
      site_contact_phone: data.contactPhone,
      street_no: data.address?.streetNumber || address?.streetNumber || '',
      street_no_suffix: data.address?.streetNumberSuffix || '',
      street_name: data.address?.streetName || address?.streetName || '',
      street_type: data.address?.streetType || address?.streetType || '',
      street_unit_no: data.address?.unitNumber || '',
      quadrant: data.address?.quadrant || address?.quadrant || '',
      zip: data.address?.zip || '',
      permits,
      legacy_project: data.type === 'regular' && firstPicked?.legacy,
      legacy_project_tpia:
        data.type === 'regular' ? firstPicked?.authorizedUserId : null,
      type: data.type,
      payment_method_id: data.paymentMethodId,
      scopes: data.scopes,
      ...(data.type === 'regular' ? { when_type: data.projectType } : {}),
      ...(data.type === 'special' ? { links: data.links } : {}),
      ...(data.type === 'special' ? { scope: data.scope } : {}),
    },
    ...(data.type === 'doee' ? { job_documents: data.jobDocuments } : {}),
  };
};

export const parsePermitsByAddressResults = (
  data: Array<AppNS.Params> | null
): Array<AppNS.Permit> => {
  if (!data) {
    return [];
  }
  return data.map((item) => {
    return {
      id: item.customId,
      scope: item.workDesc || '',
      name: '',
      owner: '',
      permitNumber: item.customId,
      permitUsed: item.permitUsed,
      cluster: item.cluster,
      hasStopWorkOrder: checkStopWorkOrder(item),
      hasTPIA: checkTPIACond(item),
      legacy: checkLegacy(item),
      authorizedTo: getAuthorizedData(item).authorizedTo,
      authorizedUserId: getAuthorizedData(item).authorizedUserId,
      authorizationDate: getAuthorizedData(item).authorizationDate,
      addressId: item.address,
      inspections: item.inspections || [],
      wallCheckRequired: item.wallCheckRequired,
      wallCheckComplete: item.wallCheckComplete,
      recStatusDate: item.recStatusDate,
    } as AppNS.Permit;
  });
};

export const parseMatchingResults = (
  agencies: Array<AppNS.Params>
): Array<AppNS.MatchingResult> => {
  return agencies ? (agencies as Array<AppNS.MatchingResult>) : [];
};

const checkStopWorkOrder = (data: AppNS.Params): boolean => {
  if (!data) {
    return false;
  }

  if (checkFieldConditions(data?.addressConditions || [], 'Stop Work Order')) {
    return true;
  }

  if (checkFieldConditions(data?.capConditions || [], 'Stop Work Order')) {
    return true;
  }

  if (checkFieldConditions(data?.parcelConditions || [], 'Stop Work Order')) {
    return true;
  }

  return false;
};

const checkTPIACond = (data: AppNS.Params): boolean => {
  if (!data) {
    return false;
  }

  if (checkFieldConditions(data?.capConditions || [], 'TPIA')) {
    return true;
  }

  return false;
};

const checkRecInsps = (data: AppNS.Params): boolean => {
  if (
    !data ||
    !data.recInsps ||
    data.recInsps.length === 0 ||
    checkLegacy(data)
  ) {
    return false;
  }

  const filtered = data.recInsps.find(
    (insp: AppNS.Params) =>
      insp.inspectionStatus !== 'Cancelled' &&
      !SPECIAL_NAMES.includes(insp.inspectionType)
  );

  return !!filtered;
};

const checkLegacy = (data: AppNS.Params): boolean => {
  if (!data || !data.capConditions || data.capConditions.length === 0) {
    return false;
  }

  const filtered = data.capConditions.find(
    (cond: AppNS.Params) =>
      cond.condStatus === 'Applied' &&
      cond.condName === 'TPIA' &&
      moment(cond.authorizationDate, 'MM/DD/YYYY').isBefore(
        moment('11/1/2021', 'MM/DD/YYYY')
      )
  );

  return !!filtered;
};

const getAuthorizedData = (
  data: AppNS.Params
):
  | AppNS.Params
  | { authorizedTo: ''; authorizedUserId: ''; authorizationDate: '' } => {
  if (!data || !data.capConditions || data.capConditions.length === 0) {
    return { authorizedTo: '', authorizedUserId: '', authorizationDate: '' };
  }

  const filtered = data.capConditions.find(
    (cond: AppNS.Params) =>
      cond.condStatus === 'Applied' &&
      cond.condName === 'TPIA' &&
      moment(cond.authorizationDate, 'MM/DD/YYYY').isBefore(
        moment('11/1/2021', 'MM/DD/YYYY')
      )
  );

  return filtered
    ? {
        authorizedTo: filtered.issuedByUser,
        authorizedUserId: filtered.userId,
        authorizationDate: filtered.authorizationDate,
      }
    : { authorizedTo: '', authorizedUserId: '', authorizationDate: '' };
};

const checkFieldConditions = (
  data: Array<AppNS.Params>,
  condName: string
): boolean => {
  const res = data.find((item) => {
    return item.condName === condName && item.condStatus === 'Applied';
  });
  return res ? true : false;
};
